import React, { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowUpRight01Icon,
  CheckmarkCircle01Icon,
  CursorMagicSelection01Icon,
  Link01Icon,
  Copy01Icon,
  UserGroupIcon,
  FlashIcon,
  Shield02Icon,
  ArrowRight01Icon,
  InformationCircleIcon,
  AlertCircleIcon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveModal from '../components/ResponsiveModal';
import CreateOrderBucketModal from '../components/CreateOrderBucketModal';
import { api } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { FadeLoader } from 'react-spinners';
import ReelText from '../components/ui/ReelText';

const StatCard = ({ title, value, icon: Icon, subValue, trend }) => (
  <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-6 rounded-3xl border border-light-gray/20 dark:border-dark-border/20 transform hover:-translate-y-1 hover:shadow-float transition-all duration-300 ease-in-out">
    <div className="flex justify-between items-start mb-4">
      <div className="p-3 bg-soft-green/10 rounded-2xl">
        <HugeiconsIcon icon={Icon} size={24} className="text-soft-green dark:text-light-mint" />
      </div>
      {trend && (
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
          trend > 0 ? 'bg-green-100/50 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100/50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <h3 className="text-sm text-muted-gray font-bold uppercase tracking-wider">{title}</h3>
    <div className="mt-1 min-w-0">
      <div className="text-2xl font-black text-charcoal dark:text-white truncate max-w-full" title={value}>
        <ReelText text={value} />
      </div>
      {subValue && <p className="text-[10px] text-muted-gray font-bold uppercase tracking-tight mt-0.5">{subValue}</p>}
    </div>
  </div>
);

const LinkCard = ({ link }) => {
  const [copied, setCopied] = useState(false);
  const percent = Math.min((parseFloat(link.filled_amount_usd) / parseFloat(link.total_amount_usd)) * 100, 100);

  const handleCopy = () => {
    navigator.clipboard.writeText(`${window.location.origin}/order/pay/${link.link_token}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-black/20 backdrop-blur-md p-4 rounded-2xl flex items-center justify-between border border-white/10 group/link">
      <div className="min-w-0 pr-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono font-bold text-sm uppercase truncate">{link.link_token}</span>
          <span className={`text-[8px] uppercase font-black px-2 py-0.5 rounded shrink-0 ${link.transaction_type === 'deposit' ? 'bg-blue-500/30 text-blue-100' : 'bg-purple-500/30 text-purple-100'}`}>
            {link.transaction_type.slice(0, 3)}
          </span>
        </div>
        <p className="text-[10px] font-bold text-white/70 truncate">₦{link.current_rate}/$ • {percent > 0 && percent < 1 ? '<1' : percent.toFixed(0)}% Filled</p>
      </div>
      <button 
        onClick={handleCopy}
        className={`p-2.5 rounded-xl transition-all shadow-sm shrink-0 ${copied ? 'bg-soft-green text-white' : 'bg-white/10 hover:bg-white/30 text-white'}`}
        title="Copy Link"
      >
        <HugeiconsIcon icon={copied ? CheckmarkCircle01Icon : Copy01Icon} size={16} />
      </button>
    </div>
  );
};

const AgentDashboard = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [showCopied, setShowCopied] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isOrderLinkModalOpen, setIsOrderLinkModalOpen] = useState(false);
  const [generatedLinks, setGeneratedLinks] = useState([]);
  const [newestLink, setNewestLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState('not_started');
  const [verificationLoading, setVerificationLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalVolumeProcessed: 0,
    activeBuckets: 0,
    exhaustedBuckets: 0,
    lifetimeVolume: 0,
  });

  const [agentBanks, setAgentBanks] = useState([]);

  const agentName = user?.business_name || (user?.email ? user.email.split('@')[0] : 'Agent');

  const calculateMetrics = (ordersList, lifetimeVolume = 0) => {
    let totalVolumeProcessed = 0;
    let activeBuckets = 0;
    let exhaustedBuckets = 0;

    ordersList.forEach(order => {
      totalVolumeProcessed += parseFloat(order.filled_amount_usd || 0);
      
      if (order.is_exhausted) {
        exhaustedBuckets += 1;
      } else if (order.status === 'active') {
        activeBuckets += 1;
      }
    });

    setMetrics({ totalVolumeProcessed, activeBuckets, exhaustedBuckets, lifetimeVolume });
  };

  const fetchData = async (background = false) => {
    if (!background) setLoading(true);
    try {
      const [ordersRes, statusRes, banksRes] = await Promise.allSettled([
        api.get('/agent/orders'),
        api.get('/agent/verification/account-status'),
        api.get('/bank-accounts')
      ]);

      if (ordersRes.status === 'fulfilled') {
        const allOrders = ordersRes.value.data.orders || [];
        const lifetimeVolume = ordersRes.value.data.metrics?.lifetime_volume_usd || 0;
        const sortedOrders = [...allOrders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        calculateMetrics(sortedOrders, lifetimeVolume);
        const activeOrders = sortedOrders.filter(o => o.status !== 'cancelled');
        setGeneratedLinks(activeOrders);
      }

      if (statusRes.status === 'fulfilled') {
        const status = statusRes.value.data?.verification_status || 'not_started';
        setIsVerified(statusRes.value.data?.is_verified);
        setVerificationStatus(status);
      }

      if (!background && banksRes.status === 'fulfilled') {
        const banksData = banksRes.value.data;
        const banks = Array.isArray(banksData) ? banksData : (banksData?.data || []);
        setAgentBanks(banks);
      }
    } catch (err) {
      console.error('Dashboard fatal error:', err);
    } finally {
      setLoading(false);
      setVerificationLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 10000); // 10s Silent Sync
    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <FadeLoader color="#4ade80" />
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/10 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/20 relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="text-4xl font-black text-charcoal dark:text-white tracking-tight">Welcome back, <ReelText text={agentName} /></h1>
          <p className="text-muted-gray dark:text-gray-300 mt-2 font-medium text-lg">Your direct funding portal is ready.</p>
        </div>
      </div>

      {/* Verification Banners */}
      {!isVerified && !verificationLoading && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-700/50 backdrop-blur-md p-6 rounded-[2.5rem] flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="bg-yellow-500 text-white p-3 rounded-2xl shadow-lg">
              <HugeiconsIcon icon={Shield02Icon} size={24} />
            </div>
            <div>
              <h3 className="font-black text-yellow-700 dark:text-yellow-300 text-lg">
                {verificationStatus === 'pending_verification' ? 'Verification Pending' : 'Activate Your Account'}
              </h3>
              <p className="text-muted-gray dark:text-gray-300 text-sm font-medium">
                {verificationStatus === 'pending_verification' 
                  ? 'Your submission is under review.' 
                  : 'Complete your verification to start trading.'}
              </p>
            </div>
          </div>
          {verificationStatus !== 'pending_verification' && (
            <Button onClick={() => navigate('/agent-fee')} className="bg-yellow-600 hover:bg-yellow-700 text-white rounded-2xl px-8 py-4 font-black">
              Verify Now
            </Button>
          )}
        </div>
      )}

      {/* Metrics Grid - 2x2 on mobile, 4 columns on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard title="Exhausted" value={metrics.exhaustedBuckets} icon={CheckmarkCircle01Icon} subValue="Capacity Reached" />
        <StatCard title="Active Links" value={metrics.activeBuckets} icon={CursorMagicSelection01Icon} subValue="Currently Open" />
        <StatCard title="Live Volume" value={`$${metrics.totalVolumeProcessed.toLocaleString()}`} icon={ArrowUpRight01Icon} subValue="Total Live Links" />
        <StatCard title="Lifetime Volume" value={`$${metrics.lifetimeVolume.toLocaleString()}`} icon={FlashIcon} subValue="All Time Processed" />
      </div>

      {/* Action Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-soft-green text-white p-8 rounded-[2.5rem] relative overflow-hidden shadow-float group flex flex-col justify-between">
          <div className="absolute top-0 right-0 p-8 opacity-10 transform group-hover:scale-110 transition-transform duration-500">
            <HugeiconsIcon icon={Link01Icon} size={120} />
          </div>
          <div className="relative z-10 mb-6">
            <h2 className="text-2xl font-black mb-6">Your Order Links</h2>
            <Button 
              onClick={() => isVerified && setIsOrderLinkModalOpen(true)}
              disabled={!isVerified}
              variant="secondary" 
              className={`w-full py-4 rounded-2xl gap-3 backdrop-blur-md text-sm font-black transition-all shadow-xl ${
                isVerified ? 'bg-white text-soft-green hover:bg-off-white border-none' : 'bg-white/10 text-white/40 border border-white/10 cursor-not-allowed'
              }`}
            >
              <HugeiconsIcon icon={FlashIcon} size={20} />
              {isVerified ? 'Create New Link' : 'Verify Account to Start'}
            </Button>
          </div>

          {/* Top 3 Recent Links */}
          <div className="relative z-10 space-y-3">
            {generatedLinks.slice(0, 3).map(link => (
              <LinkCard key={link.link_token} link={link} />
            ))}
            
            {generatedLinks.length > 3 && (
              <button onClick={() => navigate('/requests')} className="text-xs font-bold text-white/80 hover:text-white underline decoration-white/30 hover:decoration-white transition-all w-full text-center mt-2">
                See all links
              </button>
            )}
          </div>
        </div>

        <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm flex flex-col justify-center text-center">
          <div className="bg-purple-500/10 p-4 rounded-3xl inline-block mx-auto mb-4 text-purple-500">
            <HugeiconsIcon icon={UserGroupIcon} size={32} />
          </div>
          <h3 className="text-xl font-black text-charcoal dark:text-white mb-1">Direct Hub</h3>
          <p className="text-muted-gray text-sm mb-4">Manage active requests and links.</p>
          <Button onClick={() => navigate('/requests')} variant="ghost" className="text-soft-green font-black">Open Orders Hub</Button>
        </div>
      </div>

      {/* Create Order Bucket Modal */}
      <CreateOrderBucketModal
        isOpen={isOrderLinkModalOpen}
        onClose={() => {
          setIsOrderLinkModalOpen(false);
          setNewestLink(null);
        }}
        onOrderCreated={(data) => {
          const link = data?.data?.link || data?.link;
          if (link) {
            setNewestLink({ url: link });
            setIsLinkModalOpen(true);
          }
          setIsOrderLinkModalOpen(false);
        }}
        agentBanks={agentBanks}
      />
    </div>
  );
};

export default AgentDashboard;